import PubSub from '../PubSub'

export interface SearchDetails {
  searchQuery: string
  searchAlgoliaQueryId: string
  searchAlgoliaAutosuggestQueryId: string
  shouldTriggerSearchCompleteEvent: boolean
}

interface SiteState {
  isScrollingDown: boolean
  isScrolledDown: boolean
  isSearchMode: boolean
  searchDetails: SearchDetails
  showMobileMenu: boolean
  /**
   * How many DS bottom sheets currently own the screen. Published so chrome that would still show
   * through a sheet's translucent scrim — the app-push banner at 48, just under web-ui's hardcoded
   * `zIndex: 50` sheet tier — can get out of the way (STRFRNT-16018).
   *
   * A counter, not a boolean: `ModalDS` publishes for every sheet it renders, so sheets can overlap
   * (a confirmation opened from an already-open sheet). With a boolean the inner sheet's close would
   * clear the flag while the outer one is still up, and the banner would reappear over it.
   */
  dsBottomSheetOpenCount: number
}

class SiteStateManager extends PubSub {
  private state: SiteState

  constructor() {
    super()
    this.state = {
      isScrollingDown: false,
      isScrolledDown: false,
      isSearchMode: false,
      searchDetails: {
        searchQuery: '',
        searchAlgoliaQueryId: '',
        searchAlgoliaAutosuggestQueryId: '',
        shouldTriggerSearchCompleteEvent: false,
      },
      showMobileMenu: false,
      dsBottomSheetOpenCount: 0,
    }
  }

  setIsScrollingDown = (val: boolean) => {
    this.state.isScrollingDown = val
    this.publish()
  }

  getIsScrollingDown = () => {
    return this.state.isScrollingDown
  }

  setIsScrolledDown = (val: boolean) => {
    this.state.isScrolledDown = val
    this.publish()
  }

  getIsScrolledDown = () => {
    return this.state.isScrolledDown
  }

  setIsSearchMode = (val: boolean) => {
    this.state.isSearchMode = val
    this.publish()
  }

  getIsSearchMode = () => {
    return this.state.isSearchMode
  }

  setSearchAlgoliaQueryId = ({
    searchQuery,
    searchAlgoliaQueryId,
  }: {
    searchQuery: string
    searchAlgoliaQueryId: string
  }) => {
    const newSearchDetails = {
      ...this.state.searchDetails,
    }
    if (searchQuery) {
      newSearchDetails.searchQuery = searchQuery
    }
    if (searchAlgoliaQueryId) {
      newSearchDetails.searchAlgoliaQueryId = searchAlgoliaQueryId
    }
    this.state.searchDetails = newSearchDetails
    this.publish()
  }

  setSearchAlgoliaAutosuggestQueryId = ({
    searchQuery,
    searchAlgoliaAutosuggestQueryId,
  }: {
    searchQuery: string
    searchAlgoliaAutosuggestQueryId: string
  }) => {
    const newSearchDetails = {
      ...this.state.searchDetails,
    }
    if (searchQuery) {
      newSearchDetails.searchQuery = searchQuery
    }
    if (searchAlgoliaAutosuggestQueryId) {
      newSearchDetails.searchAlgoliaAutosuggestQueryId = searchAlgoliaAutosuggestQueryId
    }
    this.state.searchDetails = newSearchDetails
    this.publish()
  }

  getSearchDetails = () => {
    return this.state.searchDetails
  }

  setShouldTriggerSearchCompleteEvent = (val: boolean) => {
    this.state.searchDetails.shouldTriggerSearchCompleteEvent = val
    this.publish()
  }

  setShowMobileMenu = (val: boolean) => {
    this.state.showMobileMenu = val
    this.publish()
  }

  getShowMobileMenu = () => {
    return this.state.showMobileMenu
  }

  /** Call once per DS bottom sheet as it opens; pair with `releaseDsBottomSheet` on close/unmount. */
  acquireDsBottomSheet = () => {
    this.state.dsBottomSheetOpenCount += 1
    this.publish()
  }

  releaseDsBottomSheet = () => {
    // Clamped at 0: an unpaired release (a double cleanup under StrictMode, or a consumer that
    // releases without acquiring) must not drive the count negative, which would swallow the next
    // real acquire and leave the banner sitting over an open sheet.
    this.state.dsBottomSheetOpenCount = Math.max(0, this.state.dsBottomSheetOpenCount - 1)
    this.publish()
  }

  getIsDsBottomSheetOpen = () => {
    return this.state.dsBottomSheetOpenCount > 0
  }
}

const siteStateManager: SiteStateManager = SiteStateManager.getInstance('siteStateManager')

export default siteStateManager
